import {
  DELIVERY_CHANNELS,
  type DeliveryChannelValue,
  getDeliveryChannel,
  isDeliveryStateEnabledForChannel,
  stateLabel,
  UNSEARCHABLE_DELIVERY_STATES,
} from '../catalog';
import type { DeliveryFilterOption, DeliveryFilterOptionGroup } from '../types';

/** Order-preserving de-dupe (Ember uses `arr.indexOf(x) === i`). */
function unique(values: readonly string[]): string[] {
  return [...new Set(values)];
}

function toOption(state: string): DeliveryFilterOption {
  return { value: state, label: stateLabel(state) };
}

/**
 * Build the grouped status-filter options for a selected channel.
 *
 * Faithful port of the Ember `stateFilters` getter: take the union of the
 * `deliveryStates` (→ "Delivery Status") and `deliveryActions` (→ "User Action
 * Status") across every *available* channel, de-dupe each list, then keep only
 * the states valid for the selected channel and drop the unsearchable ones.
 *
 * `selectedChannel` is `''` for "All channels" — the measure gate treats `''`
 * like a non-sms channel (`opened` kept, `replied` dropped), matching Ember.
 * `availableChannels` are the channel values shown in the channel dropdown.
 */
export function buildStatusOptions(
  availableChannels: readonly string[],
  selectedChannel: string,
): DeliveryFilterOptionGroup[] {
  const channels = availableChannels
    .map((value) => getDeliveryChannel(value))
    .filter((channel): channel is NonNullable<typeof channel> => channel != null);

  const keep = (state: string): boolean =>
    isDeliveryStateEnabledForChannel(state, selectedChannel) && !UNSEARCHABLE_DELIVERY_STATES.includes(state);

  const deliveryStates = unique(channels.flatMap((channel) => [...channel.deliveryStates])).filter(keep);
  const deliveryActions = unique(channels.flatMap((channel) => [...channel.deliveryActions])).filter(keep);

  return [
    { label: 'Delivery Status', options: deliveryStates.map(toOption) },
    { label: 'User Action Status', options: deliveryActions.map(toOption) },
  ];
}

/** The default available-channel set (every messaging channel), by value. */
export const ALL_DELIVERY_CHANNEL_VALUES: readonly DeliveryChannelValue[] = DELIVERY_CHANNELS.map(
  (channel) => channel.value,
);

/**
 * The channel dropdown options: "All channels" plus each known available channel.
 *
 * De-duped by label, keeping the first occurrence: `twilio` and `sms` both carry
 * the name "SMS" (matching Ember, where accounts enable only one of the two), so
 * a default list containing both would otherwise render two identical options
 * selecting different `action_type` values.
 */
export function buildChannelSelectOptions(availableChannels: readonly string[]): DeliveryFilterOption[] {
  const seenLabels = new Set<string>();
  const options: DeliveryFilterOption[] = [{ value: '', label: 'All channels' }];

  for (const value of availableChannels) {
    const channel = getDeliveryChannel(value);
    if (channel == null || seenLabels.has(channel.name)) continue;
    seenLabels.add(channel.name);
    options.push({ value: channel.value, label: channel.name });
  }

  return options;
}
